import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import defaultTexts from '../../texts/texts'
import constants from '../../config/constants'
import useWindowDimensions from '../_common/WindowDimesnions'
import { request } from '../../_network/request'
import { clientAccountsURLs } from '../../_network/apiUrls'
import { capitalizeFirstLetter } from '../_common/_helpers/string'
import CustomSwitchField from '../_common/_form_fields/CustomSwitchField'

const UsersItem = (props) => {
  const {
    id,
    roles,
    firstName,
    lastName,
    email,
    enabled,
    emailVerified,
    handleEditUserDialogOpen,
    handleDeleteUserDialogOpen,
  } = props
  const theme = useTheme()
  const { width } = useWindowDimensions()

  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'

  const [role, setRole] = useState('')
  const [roleBg, setRoleBg] = useState(theme.palette.greenSheen)
  const [isEnabled, setIsEnabled] = useState(false)

  const wrapperStyle = {
    minHeight: '60px',
    background: 'white',
    boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.25)',
    borderRadius: '50px 20px 20px 20px',
    marginBottom: '20px',
  }

  const roleStyle = {
    width: '100%',
    height: '60px',
    color: 'white',
    fontSize: width >= constants.lgBreakpoint ? '20px' : '16px',
    fontWeight: '500',
    borderRadius: '50px 0px', 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const namesStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: width >= constants.lgBreakpoint ? '20px' : '16px',
    fontWeight: '500',
    width: '100%',
    height: '40px',
    margin: '10px 0px 0px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: width >= constants.lgBreakpoint ? 'flex-start' : 'center',
    borderRight: `${theme.palette.grayX11} 1px solid`,
  }

  const emailStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: width >= constants.lgBreakpoint ? '20px' : '16px',
    fontWeight: '500',
    width: '100%',
    height: '40px',
    marginTop: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  }

  const switchContainerStyle = {
    width: '100%',
    height: '40px',
    marginTop: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    borderRight: `${theme.palette.grayX11} 1px solid`,
  }

  const switchLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: width >= constants.lgBreakpoint ? '18px' : '14px',
    fontWeight: '500',
  }

  const editUserRequest = async (postData) => {
    const url = constants.core_service_domain + '/' + clientAccountsURLs.getUsers + '/' + id      
    const request_data = { url, method: 'PUT', data: postData }    
    await request(request_data)
  }

  const handleChangeIsEnabled = async (event) => {
    const value = event.target.checked
    setIsEnabled(value)
    await editUserRequest({ enabled: value })
  }

  const handleClickEdit = () => {
    handleEditUserDialogOpen(id)
  }

  const handleClickDelete = () => {
    handleDeleteUserDialogOpen(id)
  }

  useEffect(() => {
    const _role = (roles && roles.length > 0) ? roles[0] : ''

    setRole(_role)

    if (_role === 'admin') {
      setRoleBg(theme.palette.violetsAreBlue)
    } else if (_role === 'user') {
      setRoleBg(theme.palette.oxfordBlue)
    } else {
      setRoleBg(theme.palette.greenSheen)
    }
  }, [roles, theme])

  useEffect(() => {
    setIsEnabled(enabled)
  }, [enabled])

  return (
    <>
      <Box sx={wrapperStyle}>
        <Grid container>
          <Grid size={{ xs: 12, md: 2, lg: 2 }}>
            <Box
              sx={{
                ...roleStyle,
                background: roleBg,
              }}
            >
              {capitalizeFirstLetter(role)}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 3, lg: 3 }}>
            <Box sx={namesStyle}>{firstName} {lastName}</Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <Box
              sx={{
                ...emailStyle,
                borderRight: (editPermission && role !== 'owner') ? `${theme.palette.grayX11} 1px solid` : 'none',
              }}
            >
              {email}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 2, lg: 2 }}>
            <Box
              sx={{
                ...switchContainerStyle,
                borderRight: (editPermission && (role !== 'owner' || user_role === 'owner')) ? `${theme.palette.grayX11} 1px solid` : 'none',
              }}
            >
              {
                (editPermission && role !== 'owner') &&
                <CustomSwitchField
                  label={ isEnabled ? defaultTexts.disable : defaultTexts.enable }
                  checked={isEnabled}
                  disabled={!emailVerified}
                  handleChange={handleChangeIsEnabled}
                  sxLabel={switchLabelStyle}
                />
              }
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 1, lg: 1 }}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='center'
              spacing={{ xs: 0, md: 0, lg: 1 }}
              sx={{
                width: '100%',
                height: '60px',
              }}
            >
              {
                (editPermission && (role !== 'owner' || user_role === 'owner')) &&
                <IconButton
                  sx={{ color: theme.palette.oriolesOrange }}
                  onClick={handleClickEdit}
                >
                  <EditIcon />
                </IconButton>
              }
              
              {
                (editPermission && role !== 'owner') &&
                <IconButton
                  sx={{ color: theme.palette.coralRed }}
                  onClick={handleClickDelete}
                >
                  <DeleteIcon />
                </IconButton>
              }              
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default UsersItem